import React, {useEffect, useState, useCallback} from 'react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useIntl} from 'react-intl'
import {
    Box,
    IconButton,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Heading,
    FormControl,
    FormLabel,
    Text,
    Input,
    Select,
    Button,
    RadioGroup,
    ModalFooter
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    headerBar,
    resultHeader,
    storeLocatorContent,
    storeSearchResultSection,
    storeSelectorBody,
    storeSelectorFooter,
    storeSelectorFormSection,
    storeSelectorHeader
} from './index.style'
import useSitePreference from '../../hooks/use-site-preference'
import {useSearchStores} from '@salesforce/commerce-sdk-react'
import Store from './store'
import getLatLang from './getLatLong'
import useUpdateShopperContext from './updateShopperContext'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import PropTypes from 'prop-types'

const StoreSelectorModal = (props) => {
    const {
        isOpen,
        onClose,
        setSelectedStoreDetails,
        isPdp = false,
        quantity = 1,
        masterProductId = '',
        productId = '',
        isMaster = false,
        handleUpdateSore,
        states: filteredStates = []
    } = props
    const {data: customer} = useCurrentCustomer()

    const closeModal = () => {
        setEnableSearchStore(false)
        onClose()

        // reset states
        setStoreList([])
        setZipCode('')
        setZipCodeIsValid(true)
        setState('')
        setCity('')
        setFindStoreError('')
        setSelectedStore({})
        setSelectStoreError(false)
        setCheckErrors(false)
        setEnableSearchStore(false)
        setParameters({})
        setFindStoreError('')
        setDefaultStoreId()
    }

    const intl = useIntl()

    // call site preference api to get states list and maxDistance for search stores api
    const {data} = useSitePreference('statesMapping,storeLookupMaxDistance')
    const statesList = data?.statesMapping
    const maxDistance = data?.storeLookupMaxDistance

    // form states
    const [zipCode, setZipCode] = useState('')
    const [zipCodeIsValid, setZipCodeIsValid] = useState(true)
    const [state, setState] = useState('')
    const [states, setStates] = useState([])
    const [city, setCity] = useState('')
    const [findStoreError, setFindStoreError] = useState('')
    const [selectedStore, setSelectedStore] = useState({})
    const [selectStoreError, setSelectStoreError] = useState(false)
    const [checkErrors, setCheckErrors] = useState(false)
    const [enableSearchStore, setEnableSearchStore] = useState(false)
    const [parameters, setParameters] = useState({})
    const [storeList, setStoreList] = useState([])
    const [defaultStoreId, setDefaultStoreId] = useState('')
    const statesToUse = props.states && props.states.length > 0 ? props.states : states

    // api hook for seearch stores
    const {
        data: storeData,
        isLoading,
        error: errorStoreSearch
    } = useSearchStores(
        {
            parameters: parameters
        },
        {
            enabled: enableSearchStore
        }
    )

    // call shopper context api hook
    const {updateShopperContext, loading, error, shopperContext} = useUpdateShopperContext()

    const handleUpdateStoreDataToContext = useCallback(() => {
        const storeDetails = {
            name: selectedStore?.name,
            address1: selectedStore?.address1,
            city: selectedStore?.city,
            stateCode: selectedStore?.stateCode,
            postalCode: selectedStore?.postalCode,
            countryCode: selectedStore?.countryCode,
            id: selectedStore?.id,
            customerType: customer.customerType
        }
        updateShopperContext(selectedStore?.id, JSON.stringify(storeDetails))
    }, [selectedStore, updateShopperContext])

    const validateZipCode = (value) => {
        const postalPattern =
            /(^\d{5}(-\d{4})?$)|(^[abceghjklmnprstvxyABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Za-z]{1} *\d{1}[A-Za-z]{1}\d{1}$)/
        let isValid = true
        if (value?.length == 0) {
            isValid = true
            setZipCodeIsValid(true)
        } else {
            isValid = postalPattern.test(value)
            setZipCodeIsValid(postalPattern.test(value))
        }
        return isValid
    }

    const onChangeZipCode = (e) => {
        const value = e.target.value
        if (value?.length <= 5) {
            setZipCode(value)
            if (checkErrors) validateZipCode(value)
        }
    }

    const onChangeCity = (e) => {
        setCity(e.target.value)
    }

    const onChangeState = (e) => {
        setState(e.target.value)
    }

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleFindStores()
        }
    }

    const getStoresData = async (useZipCodeForGeo) => {
        if (useZipCodeForGeo) {
            // get lat and long from google api using zipcode
            const data = await getLatLang(zipCode)

            if (data?.success) {
                const param = {
                    countryCode: 'US',
                    postalCode: zipCode,
                    maxDistance: maxDistance,
                    latitude: data?.lat,
                    longitude: data?.long,
                    distanceUnit: 'mi',
                    c_isPLP: true
                }
                if (isPdp) {
                    delete param.c_isPLP
                    param.c_bopisSetStoreRequest = true
                    param.c_qty = quantity
                    param.c_pid = productId
                }
                setParameters(param)

                // enable this flag to call the store search api hook
                setEnableSearchStore(true)
            } else {
                setFindStoreError('No results found')
            }
        } else {
            // get lat and long from google api using city and state
            const data = await getLatLang(`${city}, ${state}`)

            if (data?.success) {
                // set parametes when city and state is eneterd
                const param = {
                    countryCode: 'US',
                    c_city: city,
                    c_stateCode: state,
                    maxDistance: maxDistance,
                    latitude: data?.lat,
                    longitude: data?.long,
                    distanceUnit: 'mi',
                    c_isPLP: true
                }
                if (isPdp) {
                    delete param.c_isPLP
                    param.c_bopisSetStoreRequest = true
                    param.c_qty = quantity
                    param.c_pid = productId
                }
                setParameters(param)

                // enable this flag to call the store search api hook
                setEnableSearchStore(true)
            } else {
                setFindStoreError('No results found')
            }
        }
    }
    const handleFindStores = () => {
        setCheckErrors(true)
        const isValid = validateZipCode(zipCode)
        const fieldsHaveValidEntry = zipCode !== '' || (city !== '' && state !== '')
        if (state !== '' && city === '') {
            setFindStoreError(
                intl.formatMessage({
                    id: 'plp.storeSelector.cityRequired.error.label',
                    defaultMessage: 'A City is required for search results.'
                })
            )
        } else if (city !== '' && state === '') {
            setFindStoreError(
                intl.formatMessage({
                    id: 'plp.storeSelector.stateRequired.error.label',
                    defaultMessage: 'A State is required for search results.'
                })
            )
        } else if (!fieldsHaveValidEntry) {
            setFindStoreError(
                intl.formatMessage({
                    id: 'plp.storeSelector.fieldInValid.error.label',
                    defaultMessage: "We're sorry, we couldn't find results for your search."
                })
            )
        } else {
            const useZipCodeForGeo = zipCode !== ''
            if (isValid) {
                getStoresData(useZipCodeForGeo)
            }
        }
    }

    const transformStatesList = (statesList) => {
        let states = []
        for (let key in statesList) {
            states.push({code: key, name: statesList[key]})
        }
        setStates(states)
    }

    const onChangeSelectedStore = (storeId) => {
        const store = storeList.find((s) => s.id === storeId)
        setSelectedStore(store)
    }

    const handleSelectStore = () => {
        setSelectStoreError(!selectedStore?.id)
        if (selectedStore?.id) {
            setSelectedStoreDetails && setSelectedStoreDetails(selectedStore)
            handleUpdateSore && handleUpdateSore(selectedStore?.id)
            handleUpdateStoreDataToContext()
            closeModal()
        }
    }

    useEffect(() => {
        if (!isLoading && enableSearchStore) {
            if (errorStoreSearch) {
                setFindStoreError('No results found')
                setStoreList([])
            } else if (!storeData?.data) {
                setFindStoreError('No results found')
                setStoreList([])
            } else if (storeData?.data?.length > 0) {
                setStoreList(storeData.data)
                setFindStoreError('')
                setZipCode('')
                setCity('')
                setState('')
            }
            setEnableSearchStore(false)
        }
    }, [storeData, enableSearchStore, isLoading, errorStoreSearch])

    useEffect(() => {
        if (statesList) transformStatesList(statesList)
    }, [statesList])

    // select radio for default selected store for PDP when product is not mastet
    useEffect(() => {
        if (isPdp && storeList) {
            const deafultStore = storeList?.find(
                (store) => store?.c_isDefaultStore || store?.c_selected
            )
            if (deafultStore) {
                setDefaultStoreId(deafultStore?.id)
                setSelectedStore(deafultStore)
            }
            if (!deafultStore) {
                if (customer?.isRegistered) {
                    // registered user
                    if (
                        !shopperContext?.customQualifiers?.storeDetails &&
                        !customer?.c_preferredStore
                    ) {
                        // have store available in shopper context
                        setDefaultStoreId(storeList[0]?.id)
                        setSelectedStore(storeList[0])
                    }
                } else {
                    // guest user
                    if (!shopperContext?.customQualifiers?.storeDetails) {
                        // have store available in shopper context
                        setDefaultStoreId(storeList[0]?.id)
                        setSelectedStore(storeList[0])
                    }
                }
            }
        }
    }, [isPdp, storeList])

    return (
        <Modal isOpen={isOpen} onClose={closeModal} size={'3xl'} closeOnOverlayClick={false}>
            <ModalOverlay backgroundColor={'#2d2c2cd9'} />
            <ModalContent sx={storeLocatorContent}>
                <ModalHeader sx={storeSelectorHeader}>
                    <Heading as="h2">
                        {intl.formatMessage({
                            id: 'plp.storeSelector.chooseMyStore',
                            defaultMessage: 'Choose My Store'
                        })}
                    </Heading>
                    <IconButton
                        icon={
                            <img
                                src={getAssetUrl('static/img/plp/close.webp')}
                                height="15"
                                width="15"
                                alt="Cavender's"
                            />
                        }
                        variant="unstyled"
                        onClick={closeModal}
                        className="closeButton"
                    />
                </ModalHeader>
                <ModalBody sx={storeSelectorBody}>
                    {/* form section starts here */}
                    <Box className="formSectionWrapper">
                        <Box sx={storeSelectorFormSection}>
                            {/* zip code */}
                            <FormControl id="zip">
                                <FormLabel>Zip Code</FormLabel>
                                <Input
                                    type="number"
                                    value={zipCode}
                                    onKeyDown={handleKeyPress}
                                    onChange={onChangeZipCode}
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus
                                    placeholder="00000"
                                    autoComplete="off"
                                    className={!zipCodeIsValid && 'inputHasError'}
                                />
                                {!zipCodeIsValid && (
                                    <Text className="inputError">
                                        {intl.formatMessage({
                                            id: 'plp.storeSelector.zipCodeText.error.label',
                                            defaultMessage: 'Invalid zip code.'
                                        })}
                                    </Text>
                                )}
                            </FormControl>

                            {/* or divider */}
                            <Text as="p" className="orDivider">
                                {intl.formatMessage({
                                    id: 'plp.storeSelector.or',
                                    defaultMessage: 'OR'
                                })}
                            </Text>

                            {/* city and state container */}
                            <Box className="stateCity">
                                {/* city */}
                                <FormControl className="city" id="city">
                                    <FormLabel>City</FormLabel>
                                    <Input
                                        type="text"
                                        value={city}
                                        placeholder="City"
                                        onKeyDown={handleKeyPress}
                                        onChange={onChangeCity}
                                        autoComplete="on"
                                    />
                                </FormControl>

                                {/* state */}
                                <FormControl className="state" id="state">
                                    <FormLabel>State</FormLabel>
                                    <Select
                                        placeholder="State"
                                        value={state}
                                        onChange={onChangeState}
                                    >
                                        {statesToUse.map(({code, name}) => (
                                            <option key={code} value={code}>
                                                {name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Button
                                className="findStoresButton"
                                variant={'outline'}
                                onClick={handleFindStores}
                            >
                                {intl.formatMessage({
                                    id: 'storeSelector.button.findStores.label',
                                    defaultMessage: 'FIND STORES'
                                })}
                            </Button>
                        </Box>
                    </Box>
                    {/* form section ends here */}

                    {/* error message */}
                    {findStoreError && (
                        <Text className="errorMessage">
                            {intl.formatMessage(
                                {
                                    id: 'plpl.storeSelector.findStoresButton.error.label',
                                    defaultMessage: '{error}'
                                },
                                {error: findStoreError}
                            )}
                        </Text>
                    )}

                    {/* store result section starts here */}
                    {storeList?.length > 0 && !findStoreError && (
                        <>
                            {/* total results heading */}
                            <Heading as="h2" className="result">
                                {storeList?.length}{' '}
                                {storeList?.length > 1
                                    ? intl.formatMessage({
                                          id: 'plp.storeSelector.resultsCount.single.label',
                                          defaultMessage: ' RESULTS'
                                      })
                                    : intl.formatMessage({
                                          id: 'plp.storeSelector.resultsCount.notSingle.label',
                                          defaultMessage: ' RESULT'
                                      })}
                            </Heading>

                            <Box sx={storeSearchResultSection}>
                                <Box sx={headerBar}>
                                    {/* store name */}
                                    <Heading as="h2" sx={resultHeader}>
                                        {intl.formatMessage({
                                            id: 'plp.storeSelector.resultList.storeName.label',
                                            defaultMessage: 'STORE NAME'
                                        })}
                                    </Heading>

                                    {/* availability name */}
                                    {isPdp && (
                                        <Heading as="h2" sx={resultHeader}>
                                            {intl.formatMessage({
                                                id: 'plp.storeSelector.resultList.availability.label',
                                                defaultMessage: 'AVAILABILITY'
                                            })}
                                        </Heading>
                                    )}
                                </Box>

                                {/* iterate on each store to show list of stores found */}
                                {storeList?.length > 0 && (
                                    <RadioGroup
                                        onChange={onChangeSelectedStore}
                                        value={selectedStore?.id}
                                    >
                                        {storeList?.map((store) => (
                                            <Store
                                                store={store}
                                                isMaster={isMaster}
                                                isPdp={isPdp}
                                                isDefaultStore={defaultStoreId == store?.id}
                                                key={store.id}
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            </Box>
                        </>
                    )}
                    {/* store result section ends here */}
                </ModalBody>

                {/* modal footer containing button to select store */}
                {storeList?.length > 0 && !findStoreError && (
                    <ModalFooter sx={storeSelectorFooter}>
                        {selectStoreError && (
                            <Text className="selectStoreError">
                                {intl.formatMessage({
                                    id: 'plpl.storeSelector.storeSelected.error.label',
                                    defaultMessage: 'Select at least one store'
                                })}
                            </Text>
                        )}
                        <Button
                            className="selectStoreButton"
                            colorScheme="blue"
                            mr={3}
                            onClick={handleSelectStore}
                        >
                            SELECT STORE
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    )
}

StoreSelectorModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    setSelectedStoreDetails: PropTypes.func,
    isPdp: PropTypes.bool,
    quantity: PropTypes.number,
    masterProductId: PropTypes.string,
    productId: PropTypes.string,
    isMaster: PropTypes.bool,
    handleUpdateSore: PropTypes.func,
    states: PropTypes.array
}

export default StoreSelectorModal
